import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { getRoleLabel } from "@/utils/roleLabel";

import { useDriveUsers, useSetQuota } from "../hooks";

const GB = 1024 ** 3;

interface Props {
  open: boolean;
  onClose: () => void;
}

/** CEO-only: raise or lower a user's TwoDrive storage pool. */
export function CeoQuotaPanel({ open, onClose }: Props) {
  // Lista z dysku: pracownicy GK i podwykonawcy wpuszczeni flagą. Konta bez wstępu
  // na dysk celowo się tu nie pokazują — serwer i tak odmówiłby ustawienia limitu.
  const { data: users = [] } = useDriveUsers();
  const setQuota = useSetQuota();
  const [userId, setUserId] = useState("");
  const [gb, setGb] = useState("50");
  const [done, setDone] = useState(false);

  const selected = users.find((u) => u.id === userId);

  // Pole startuje od limitu, który konto ma DZIŚ — inaczej CEO wpisuje w ciemno
  // i łatwo przez przypadek ściąć komuś pulę do widocznych „50".
  function pick(id: string) {
    setUserId(id);
    const user = users.find((u) => u.id === id);
    if (user?.quota_bytes) setGb(String(Math.round((user.quota_bytes / GB) * 100) / 100));
  }

  function save() {
    if (!userId) return;
    setQuota.mutate(
      { userId, quotaBytes: Math.round(Number(gb) * GB) },
      {
        onSuccess: () => {
          setDone(true);
          window.setTimeout(() => setDone(false), 2500);
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Limity przestrzeni użytkowników" size="md">
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Użytkownik
          </span>
          <select
            className="input-base"
            value={userId}
            onChange={(e) => pick(e.target.value)}
          >
            <option value="">— wybierz —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name} ({getRoleLabel(u.role)})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Limit (GB)
          </span>
          {selected?.is_contractor && (
            <span className="text-xs text-[var(--color-text-muted)]">
              Konto podwykonawcy — domyślna pula to 10 GB.
            </span>
          )}
          <input
            type="number"
            min={0}
            step={1}
            className="input-base"
            value={gb}
            onChange={(e) => setGb(e.target.value)}
          />
        </label>

        <div className="flex items-center justify-between">
          {done ? (
            <span className="text-xs font-mono text-green-600">Zapisano.</span>
          ) : (
            <span />
          )}
          <Button onClick={save} disabled={!userId} loading={setQuota.isPending}>
            Zapisz limit
          </Button>
        </div>
      </div>
    </Modal>
  );
}
